// src/components/relatorios/DashboardExecutivo.js
import React, { useState } from 'react';
import api from '../../services/api';

function DashboardExecutivo({ data, canal = 'geral' }) { // 🆕 RECEBE CANAL
  const [mostrarModalMeta, setMostrarModalMeta] = useState(false);
  const [sugestaoMeta, setSugestaoMeta] = useState(null);
  const [carregandoSugestao, setCarregandoSugestao] = useState(false);
  const [editandoMeta, setEditandoMeta] = useState(false);
  const [novoValorMeta, setNovoValorMeta] = useState('');

  if (!data) return null;

  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(n(valor));
  };

  const formatarPercentual = (valor) => {
    const num = n(valor);
    const sinal = num > 0 ? '+' : '';
    return `${sinal}${num.toFixed(1)}%`;
  };

  const getClasseTendencia = (valor) => {
    const num = n(valor);
    if (num > 0) return 'tendencia-positiva';
    if (num < 0) return 'tendencia-negativa';
    return 'tendencia-neutra';
  };

  // 🆕 FUNÇÃO: Retorna a chave correta da meta baseada no canal
  const getChaveMeta = () => {
    if (canal === 'presencial') {
      return 'meta_mensal_faturamento_presencial';
    } else if (canal === 'licitacao') {
      return 'meta_mensal_faturamento_licitacao';
    } else if (canal === 'marketplace') {
      return 'meta_mensal_faturamento_marketplace';
    } else {
      return 'meta_mensal_faturamento'; // GERAL
    }
  };

  // 🆕 FUNÇÃO: Retorna o nome do canal
  const getNomeCanal = () => {
    const nomes = {
      'geral': 'GERAL',
      'presencial': 'PRESENCIAL',
      'licitacao': 'LICITAÇÕES',
      'marketplace': 'MARKETPLACE'
    };
    return nomes[canal] || 'GERAL';
  };

  // ========= META (FATURAMENTO) =========
  const metaValor = n(data.meta?.valor);
  const metaAtual = n(data.meta?.faturado);
  const metaPercentual = n(data.meta?.percentual);
  const metaAtingiu = !!data.meta?.atingiu;

  // ========= LUCRO =========
  const lucroBruto = n(data.lucro?.valor);
  const lucroLiquido = n(data.lucro?.liquido);
  const margemBruta = n(data.lucro?.margem);
  const margemLiquida = n(data.lucro?.margem_liquida);

  // ========= CUSTOS =========
  const custos = data.lucro?.custos || {};
  const custoProdutos = n(custos.produtos);
  const custoFrete = n(custos.frete);
  const custoTerceiros = n(custos.terceiros);
  const custoImpostos = n(custos.impostos);
  const impostosProdutos = n(custos.impostos_produtos);
  const impostosServicos = n(custos.impostos_servicos);

  const calcularProximaMeta = async () => {
    try {
      setCarregandoSugestao(true);
      setMostrarModalMeta(true);

      // 🆕 Enviar canal na requisição
      const resp = await api.post('/configuracoes/meta/calcular-proxima', {
        canal: canal
      });
      const result = resp.data;

      if (result?.success) {
        setSugestaoMeta(result.data);
        setNovoValorMeta(String(result.data.proxima_meta ?? ''));
      } else {
        alert('Erro ao calcular próxima meta');
      }
    } catch (error) {
      console.error('Erro ao calcular meta:', error);
      alert('Erro ao calcular próxima meta');
    } finally {
      setCarregandoSugestao(false);
    }
  };

  const salvarNovaMeta = async () => {
    try {
      // 🆕 GERAL não pode ser editado
      if (canal === 'geral') {
        alert('⚠️ A meta GERAL é calculada automaticamente.\n\nEdite as metas individuais de cada canal:\n• Presencial\n• Licitações\n• Marketplace');
        return;
      }

      const valor = n(novoValorMeta);

      if (!Number.isFinite(valor) || valor <= 0) {
        alert('Por favor, insira um valor válido');
        return;
      }

      // 🆕 Salvar na chave correta do canal
      const chaveMeta = getChaveMeta();
      const resp = await api.put(`/configuracoes/${chaveMeta}`, {
        valor: String(valor)
      });

      const result = resp.data;

      if (result?.success) {
        alert(`✅ Meta de ${getNomeCanal()} atualizada! Recarregue a página para ver as mudanças.`);
        setMostrarModalMeta(false);
        setEditandoMeta(false);
      } else {
        alert('Erro ao salvar meta');
      }
    } catch (error) {
      console.error('Erro ao salvar meta:', error);
      alert('Erro ao salvar meta');
    }
  };

  const fecharModal = () => {
    setMostrarModalMeta(false);
    setEditandoMeta(false);
    setSugestaoMeta(null);
  };

  const kpis = [
    {
      titulo: '💰 Faturamento do Mês',
      valor: formatarValor(data.faturamento?.valor),
      subtitulo: `${n(data.faturamento?.total_os)} OS faturadas`,
      tendencia: formatarPercentual(data.faturamento?.percentual),
      classeTendencia: getClasseTendencia(data.faturamento?.percentual),
      cor: 'blue'
    },
    {
      titulo: '💎 Lucro Líquido',
      valor: formatarValor(lucroLiquido),
      subtitulo: `Margem líquida: ${margemLiquida.toFixed(1)}%`,
      tendencia: null,
      cor: 'green'
    },
    {
      titulo: '📊 Pipeline',
      valor: formatarValor(data.pipeline?.valor),
      subtitulo: 'Em negociação',
      tendencia: null,
      cor: 'purple'
    },
    {
      titulo: `🎯 Meta ${getNomeCanal()}`, // 🆕 Mostra nome do canal
      valor: `${metaPercentual.toFixed(1)}%`,
      subtitulo: `${formatarValor(metaAtual)} / ${formatarValor(metaValor)}`,
      tendencia: metaAtingiu ? '✓ Atingida' : null,
      classeTendencia: metaAtingiu ? 'tendencia-positiva' : 'tendencia-neutra',
      cor: metaAtingiu ? 'green' : 'orange'
    },
    {
      titulo: '🎫 Ticket Médio',
      valor: formatarValor(data.ticket_medio),
      subtitulo: 'Por OS fechada',
      tendencia: null,
      cor: 'indigo'
    },
    {
      titulo: '💼 Backlog',
      valor: formatarValor(data.backlog?.valor),
      subtitulo: `${n(data.backlog?.total_os)} OS`,
      tendencia: null,
      cor: 'teal'
    }
  ];

  return (
    <div className="dashboard-executivo">
      <div className="kpis-grid">
        {kpis.map((kpi, index) => (
          <div key={index} className={`kpi-card border-${kpi.cor}`}>
            <div className="kpi-header">
              <span className="kpi-titulo">{kpi.titulo}</span>

              {kpi.tendencia && (
                <span className={`kpi-tendencia ${kpi.classeTendencia || ''}`}>
                  {kpi.classeTendencia
                    ? (n(data.faturamento?.percentual) > 0 ? '↗' : '↘')
                    : '•'}{' '}
                  {kpi.tendencia}
                </span>
              )}
            </div>

            <div className="kpi-valor">{kpi.valor}</div>
            <div className="kpi-subtitulo">{kpi.subtitulo}</div>

            {kpi.titulo.includes('Meta') && canal !== 'geral' && ( // 🆕 Só mostra botão se não for GERAL
              <button
                className="btn-calcular-meta"
                onClick={calcularProximaMeta}
                style={{
                  marginTop: '10px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                ⚙️ Calcular Próxima Meta
              </button>
            )}

            {/* 🆕 AVISO quando for GERAL */}
            {kpi.titulo.includes('Meta') && canal === 'geral' && (
              <div style={{
                marginTop: '10px',
                padding: '6px 12px',
                fontSize: '11px',
                background: '#eff6ff',
                color: '#1e40af',
                border: 'none',
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                ℹ️ Soma das metas dos canais
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="lucro-breakdown">
        <h4>📋 Detalhamento (Padrão Gerencial)</h4>

        <div className="lucro-items">
          <div className="lucro-item">
            <span className="lucro-label">💎 Bruto:</span>
            <span className="lucro-valor">{formatarValor(lucroBruto)}</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">🧾 Impostos:</span>
            <span className="lucro-valor">{formatarValor(custoImpostos)}</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">🚚 Frete:</span>
            <span className="lucro-valor">{formatarValor(custoFrete)}</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">👷 Terceiros:</span>
            <span className="lucro-valor">{formatarValor(custoTerceiros)}</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">📦 Produtos (custo):</span>
            <span className="lucro-valor">{formatarValor(custoProdutos)}</span>
          </div>

          <div className="lucro-item lucro-total">
            <span className="lucro-label">💰 Líquido:</span>
            <span className="lucro-valor">{formatarValor(lucroLiquido)}</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">📈 Margem bruta:</span>
            <span className="lucro-valor">{margemBruta.toFixed(1)}%</span>
          </div>

          <div className="lucro-item">
            <span className="lucro-label">📉 Margem líquida:</span>
            <span className="lucro-valor">{margemLiquida.toFixed(1)}%</span>
          </div>

          {(impostosProdutos > 0 || impostosServicos > 0) && (
            <>
              <div className="lucro-item">
                <span className="lucro-label">🧾 Imp. Produtos:</span>
                <span className="lucro-valor">{formatarValor(impostosProdutos)}</span>
              </div>
              <div className="lucro-item">
                <span className="lucro-label">🧾 Imp. Serviços:</span>
                <span className="lucro-valor">{formatarValor(impostosServicos)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {mostrarModalMeta && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={fecharModal}
        >
          <div
            style={{
              background: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '24px' }}>
              📊 Sugestão de Próxima Meta - {getNomeCanal()}
            </h2>

            {carregandoSugestao ? (
              <p>Calculando...</p>
            ) : sugestaoMeta ? (
              <>
                <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Meta Atual:</strong> {formatarValor(sugestaoMeta.meta_atual)}
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Realizado:</strong> {formatarValor(sugestaoMeta.faturado)}
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Desempenho:</strong> {n(sugestaoMeta.percentual_atingido).toFixed(1)}%
                    {n(sugestaoMeta.percentual_atingido) >= 90 ? ' ✅' : ' ❌'}
                  </div>
                  <div
                    style={{
                      padding: '15px',
                      background: n(sugestaoMeta.percentual_atingido) >= 90 ? '#d1fae5' : '#fef3c7',
                      borderRadius: '6px',
                      marginTop: '15px'
                    }}
                  >
                    <strong>{sugestaoMeta.motivo}</strong>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    Próxima Meta:
                  </label>

                  {editandoMeta ? (
                    <input
                      type="number"
                      value={novoValorMeta}
                      onChange={(e) => setNovoValorMeta(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: '2px solid #3b82f6',
                        borderRadius: '6px'
                      }}
                      autoFocus
                    />
                  ) : (
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        marginBottom: '10px'
                      }}
                    >
                      {formatarValor(sugestaoMeta.proxima_meta)}
                      {n(sugestaoMeta.aumento) !== 0 && (
                        <span
                          style={{
                            fontSize: '16px',
                            marginLeft: '10px',
                            color: n(sugestaoMeta.aumento) > 0 ? '#10b981' : '#6b7280'
                          }}
                        >
                          ({n(sugestaoMeta.aumento) > 0 ? '+' : ''}
                          {formatarValor(sugestaoMeta.aumento)})
                        </span>
                      )}
                    </div>
                  )}

                  {!editandoMeta && (
                    <button
                      onClick={() => setEditandoMeta(true)}
                      style={{
                        padding: '8px 16px',
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      ✏️ Editar Valor
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={salvarNovaMeta}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    ✅ Salvar Meta
                  </button>
                  <button
                    onClick={fecharModal}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    ❌ Cancelar
                  </button>
                </div>
              </>
            ) : (
              <p>Nenhuma sugestão disponível.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardExecutivo;